import {
  Box,
  Container,
  Text,
  Button,
  Stack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from "@chakra-ui/react";
import { ContentNotFound, Hero, Layout } from "@common";
import { ISSRPageData } from "@interfaces";
import { PageService } from "@services";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { MotionText } from "@ui";
import { fadeIn } from "@lib/motion";
import { NewsletterForm } from '@forms'

const CardHorizontal = ({
                          title,
                          description,
                          imageUrl,
                          url,
                        }: {
  title: string;
  description: string;
  imageUrl: string;
  url?: string;
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleClick = () => {
    if (url) {
      window.location.href = url;
    } else {
      onOpen();
    }
  };

  // Özel başlık kontrolü
  const isSuare2025 = title?.toLowerCase().includes("müzede suare");
  const isYoga2025 = title?.toLowerCase().includes("müzede yoga");
  const isSahne2025 = title?.toLowerCase().includes("müzede sahne");

  return (
    <>
      <Box
        position="relative"
        width="100%"
        height={{ base: "auto", lg: "430px" }}
        backgroundImage={`url(${imageUrl})`}
        backgroundSize="cover"
        backgroundPosition="center"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          zIndex="1"
          width="100%"
          height="100%"
          bg="transparent"
          backgroundImage="radial-gradient(#ffffff33 1px, transparent 1px)"
          backgroundSize="20px 20px"
        />

        <Box
          position="relative"
          width="100%"
          height="100%"
          bg="linear-gradient(180deg, rgba(13, 16, 29, 0.9) 0%, rgba(13, 16, 29, 0) 50%)"
          display="flex"
          alignItems="end"
          justifyContent="flex-start"
          padding="4"
        >
          <VStack
            align="flex-start"
            spacing={4}
            zIndex="2"
            color="white"
            maxWidth="500px"
            p={{ base: "20px", lg: "20px" }}
            textAlign="left"
            bg="rgba(199, 31, 63, 0.1)"
            border="1px solid rgba(255, 255, 255, 0.3)"
            boxShadow="0 4px 30px rgba(0, 0, 0, 0.1)"
            backdropFilter="blur(10px)"
          >
            <Box>
              <MotionText
                variants={fadeIn.up}
                fontSize={{ base: "2xl", lg: "3xl" }}
                color="whiteAlpha.primary4"
                fontWeight="bold"
                lineHeight="normal"
                as="h2"
                mb={{ base: 1, lg: "6px" }}
              >
                {title}
              </MotionText>
              <Text fontSize={{ base: "xs", md: "sm" }} opacity={0.85}>
                {description}
              </Text>

              <Button
                variant="link"
                color="white.primary2"
                size="md"
                mt="2"
                onClick={handleClick}
              >
                {(isSuare2025 || isYoga2025 || isSahne2025) && url
                  ? `Etkinlik hakkında bilgi almak için tıklayın`
                  : t("common:ticketBuy")}
              </Button>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* URL varsa modal gösterilmez */}
      {!url && (
        <Modal isOpen={isOpen} isCentered onClose={onClose} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("common:ticketBuy")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <NewsletterForm buttonLabel={t("nav:register")} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

const EventPage = ({
                     pageData,
                     global: { navigation }
                   }: ISSRPageData) => {
  const { t } = useTranslation();
  const { asPath } = useRouter();

  if (pageData.error) {
    return (
      <Layout
        title={t("common:museumProgram.tab3")}
        navigation={navigation}
        path={asPath}
      >
        <ContentNotFound infoType="serverSideError" />
      </Layout>
    );
  }

  return (
    <Layout
      title={t("common:museumProgram.tab3")}
      navigation={navigation}
      path={asPath}

    >
      <Hero
        bgImageUrl={'/img/exhibition-and-event/etkinlikler-banner.jpg'}
        title={t("common:museumProgram.tab3")}
      />

      <Container maxW="container.xl" mt={10} mb={10}>
        <Text
          as="h2"
          color="red.primary"
          fontSize={{ base: '2xl', lg: '3xl' }}
        >
          {t("common:museumEventSeries")}
        </Text>
        <Text
          as="p"
          mb={{ base: 'padding.primary', lg: 'padding.primary' }}
        >
          {t("common:eventDescription")}
        </Text>
        <Stack spacing={10}>
          <CardHorizontal
            title={t("common:StageAtTheMuseum.title")}
            description={t("common:StageAtTheMuseum.description")}
            imageUrl={'/img/exhibition-and-event/muzede-sahne.jpg'}
            url={'/sergiler-ve-etkinlikler/etkinlik/572'}

          />

          <CardHorizontal
            title={t("common:soireeAtTheMuseum.title")}
            description={t("common:soireeAtTheMuseum.description")}
            imageUrl={'/img/exhibition-and-event/muzede-suare.jpg'}
            url={'/sergiler-ve-etkinlikler/etkinlik/555'}
          />
          <CardHorizontal
            title={t("common:yogAAtTheMuseum.title")}
            description={t("common:yogAAtTheMuseum.description")}
            imageUrl="https://drupal.sakipsabancimuzesi.org/sites/default/files/styles/webp/public/2022-05/Etkinlik-banner-id-258.jpg.webp"
            url={'/sergiler-ve-etkinlikler/etkinlik/566'}

          />
          <Text
            as="h2"
            color="red.primary"
            fontSize={{ base: '2xl', lg: '3xl' }}
          >
            {t("common:corporateEvents")}
          </Text>
          <Text
            as="p"
            color="black.primary"
            sx={{
              marginTop: "10px !important",
            }}
          >
            {t("common:corporateEventsDesc")}
          </Text>

        </Stack>
      </Container>
    </Layout>
  );
};

export default EventPage;

// Sayfa Verilerini Getir
export async function getStaticProps({ locale }) {
  const pageData = {
    data: null,
    error: null
  };

  try {
    const collection = await PageService.getHomePage(locale);
    pageData.data = collection;
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message };
    }
  }

  return {
    props: { pageData },
    revalidate: 120
  };
}
